<template>
  <div class="net-footer" :class="docked ? '-docked' : '-undocked'">
    <slot></slot>

    <div class="net-footer__zoom">
      <scene-switcher :mode="netMode" />

      <scene-background-panel v-if="netMode == 'edit'" />

      <net-zoom-controls
        :scale="scale"
        @update:scale="$emit('update:scale', $event)"
      />

      <net-visibility-button />

      <template v-if="true">
        <button
          ref="layersButton"
          type="button"
          class="button-icon ph-1"
          @click="onLayersButtonClick"
        >
          <lucide-icon name="layers-2" />
        </button>

        <scene-layers-popup
          v-if="sceneLayersPopupData.visible"
          :editable="netMode == 'edit'"
          :x="sceneLayersPopupData.x"
          :y="sceneLayersPopupData.y"
          :width="sceneLayersPopupData.width"
          :height="sceneLayersPopupData.height"
          @close="sceneLayersPopupData.visible = false"
          @move="saveSceneLayersPopupPosition($event)"
          @resize="saveSceneLayersPopupPosition($event)"
        />
      </template>

      <button
        v-if="isFullscreenSupported"
        type="button"
        class="button-icon ph-1 mr-3"
        @click="$emit('request-fullscreen')"
      >
        <lucide-icon :name="isFullscreen ? 'minimize' : 'maximize'" />
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive, ref, onMounted, onBeforeUnmount, useTemplateRef } from "vue";
import { useFullscreen } from "@vueuse/core";

import LucideIcon from "@core/ui/LucideIcon.vue";
import NetVisibilityButton from "@editor/net/NetVisibilityButton.vue";
import NetZoomControls from "@editor/net/NetZoomControls.vue";
import SceneLayersPopup from "@editor/items/sceneLayer/SceneLayersPopup.vue";
import SceneSwitcher from "@editor/items/scene/SceneSwitcher.vue";
import SceneBackgroundPanel from "@editor/items/scene/SceneBackgroundPanel.vue";

import { usePopupSizes } from "@editor/store/editor/popupSizes.store";
import { useSceneTree } from "@editor/items/scene/sceneTree";

import type { NetCanvasProps } from "./NetCanvasProps";

const props = defineProps<{
  scale: number;
  netMode: NetCanvasProps["mode"];
}>();
const emit = defineEmits<{
  (event: "update:scale", value: number): void;
  (event: "request-fullscreen"): void;
}>();

const sceneTree = useSceneTree();
const popupSizes = usePopupSizes();


// ==============================
// FULLSCREEN

const { isSupported: isFullscreenSupported } = useFullscreen();

const isFullscreen = ref(document.fullscreenElement !== null);

function updateFullscreen() {
  isFullscreen.value = document.fullscreenElement !== null;
}
onMounted(() => {
  document.addEventListener("fullscreenchange", updateFullscreen);
});
onBeforeUnmount(() => {
  document.removeEventListener("fullscreenchange", updateFullscreen);
});


// ========================
// Net Layers Popup

const layersButton = useTemplateRef("layersButton");

const sceneLayersPopupData = reactive({
  x: null as number | null,
  y: null as number | null,
  width: 400 as number | null,
  height: null as number | null,
  visible: false
});

function onLayersButtonClick(mouseEvent: MouseEvent) {
  if (sceneLayersPopupData.visible) {
    sceneLayersPopupData.visible = false;
    return;
  }
  const rect = layersButton.value?.getBoundingClientRect();
  if (!rect) return;
  openSceneLayersPopup(rect.right, rect.top);
}

function openSceneLayersPopup(x: number, y: number) {
  let savedPosition = null;
  if (props.netMode == "edit") {
    savedPosition = popupSizes.netLayers;
  }
  if (savedPosition) {
    sceneLayersPopupData.x = savedPosition.x || null;
    sceneLayersPopupData.y = savedPosition.y || null;
    sceneLayersPopupData.width = savedPosition.width || null;
    sceneLayersPopupData.height = savedPosition.height || null;
  }

  if (sceneLayersPopupData.x == null) {
    sceneLayersPopupData.x = x;
  }
  if (sceneLayersPopupData.y == null) {
    sceneLayersPopupData.y = y;
  }
  if (sceneLayersPopupData.width == null) {
    sceneLayersPopupData.width = 300;
  }

  sceneLayersPopupData.visible = true;
}

function saveSceneLayersPopupPosition(position: { x: number; y: number; width?: number; height?: number }) {
  sceneLayersPopupData.x = position.x;
  sceneLayersPopupData.y = position.y;
  sceneLayersPopupData.width = position.width ?? sceneLayersPopupData.width;
  sceneLayersPopupData.height = position.height ?? sceneLayersPopupData.height;
  if (props.netMode == "edit") {
    popupSizes.updateSize("netLayers", position);
  }
}

// ========================
// Docked

const docked = computed(() => sceneTree.activeScene?.footerAnchored != false);
</script>

<style lang="scss">
.net-footer {
  display: grid;
  grid-template-columns: minmax(8rem, 1fr) min-content; //minmax(4rem, 25rem); // 5rem
  align-items: end;
  pointer-events: none;

  &.-docked {
    background-color: var(--color-background-deeper);
  }
  &.-undocked {
    background-color: transparent;
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
  }

  &__zoom {
    display: flex;
    align-items: center;
    min-height: 2.5rem;
    min-width: 2rem;
    padding-left: 1rem;
    background-color: var(--color-background-deeper);
    border-top-left-radius: 0.25rem;
    pointer-events: auto;
    gap: 0.75rem;
  }


  &__visibility-dialog {
    background-color: var(--color-background-deep);
  }
}
</style>
